import Styles from './PostPreview.module.scss'
import { Preview } from 'components/PostPreviewModal/Preview'
import { PostPreviewChannelType } from 'components/PostPreviewModal/Types'
import { forwardRef, useEffect, useMemo, memo, useState, useRef } from 'react'
import { useQueryClient } from 'react-query'
import cx from 'classnames'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { StoreSnapshot, Editor, createTLStore } from '@tldraw/tldraw'
import { ArticlePostPreview } from './ArticlePostPreview'
import { PostPreviewProps, TYPES_WITH_PREVIEW } from './Types'
import { MediaObject } from 'types'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import * as CSS from 'csstype'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import {
  channelTypeFromProvider,
  providerFromChannelType,
} from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { shapeUtils } from 'components/VisualStudioPlayground/VisualEditor/VisualEditor'
import { sha1Hex } from 'utils'
import { isBlankDesign } from 'components/Design/utils/isBlankDesign'
import { BlogPostPreviewChrome } from './BlogPostPreviewChrome/BlogPostPreviewChrome'
import { NewsletterPreview } from './NewsletterPreview/NewsletterPreview'
import { GenerationFailedPreview } from '../WeekViewV2/CalendarEventCard/GenerationFailedPreview/GenerationFailedPreview'
import {
  getVideoPlatformConfig,
  isVideoChannel,
  getCrossPostGroupForChannel,
  filterGBPFromCrossPostGroup,
  getMinWidthForChannel,
  isStoryChannel,
  calculateMaxWidth,
} from './utils'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDocumentOverlay } from 'components/Previews/components/EditInDocumentOverlay/EditInDocumentOverlay'
import { useDesignPreviewSync } from '../useDesignPreviewSync'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { prefetchDesignEditRequests } from 'hooks/queries/designs/useDesignEditRequests'

const DEFAULT_WINDOW_HEIGHT_OFFSET = 220

const PostPreviewComponent = forwardRef<HTMLDivElement, PostPreviewProps>(
  (
    {
      almanacFile,
      isLoading,
      showVideoControls,
      videoFit,
      isActive = true,
      previewPlatform,
      onClose,
      onSelectPlatform,
      isInsertingMedia,
      isTransitioning = false,
      showHoverActionOverlay = false,
      hideConnectButton = false,
      alwaysTruncateCaption = false,
      onSeeMoreToggle,
      seeMore = false,
      hasRatingOverlay = false,
      windowHeightOffset = DEFAULT_WINDOW_HEIGHT_OFFSET,
    },
    fRef,
  ) => {
    const queryClient = useQueryClient()
    const workspaceId = useCurrentWorkspaceId()
    const { isMobile } = useResponsiveDesign()
    const postPreviewHeaderRef = useRef<HTMLDivElement>(null)
    const postPreviewContentRef = useRef<HTMLDivElement>(null)
    const postPreviewFooterRef = useRef<HTMLDivElement>(null)

    // Track window height to recalculate max width on resize/zoom
    const [windowHeight, setWindowHeight] = useState(() => window.innerHeight)

    useEffect(() => {
      const handleResize = () => {
        setWindowHeight(window.innerHeight)
      }

      window.addEventListener('resize', handleResize)
      // Also listen for zoom changes (visualViewport API)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize)
      }

      return () => {
        window.removeEventListener('resize', handleResize)
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleResize)
        }
      }
    }, [])

    const isViewportReady = true

    const isCrossPost = (almanacFile?.scheduled_post_targets || []).length > 1
    const crossPostDefaultPlatform = isCrossPost ? ExternalIntegrationEnum.Instagram : undefined
    // Get the default platform for this file's channel
    const defaultPlatform = almanacFile?.channel ? providerFromChannelType[almanacFile.channel] : undefined

    // Use the selected platform if available, otherwise use the default
    const currentPreviewPlatform = previewPlatform || crossPostDefaultPlatform || defaultPlatform

    // Set default preview platform when almanacFile changes and no platform is explicitly selected
    useEffect(() => {
      if (isActive && almanacFile?.channel && !previewPlatform && onSelectPlatform) {
        const isCrosspost = (almanacFile.scheduled_post_targets || []).length > 1
        const defaultPlatform = isCrosspost
          ? ExternalIntegrationEnum.Instagram
          : providerFromChannelType[almanacFile.channel]
        if (defaultPlatform) {
          onSelectPlatform(defaultPlatform)
        }
      }
    }, [isActive, almanacFile?.channel, previewPlatform, onSelectPlatform, almanacFile?.scheduled_post_targets])

    const showVideoCrossPost = almanacFile?.channel ? isVideoChannel(almanacFile.channel as DesignChannel) : false
    const isGBPEnabled = useFlipperFlag('google_business_profile_integration')
    const crossPostGroup = filterGBPFromCrossPostGroup(
      getCrossPostGroupForChannel(almanacFile?.channel as DesignChannel),
      isGBPEnabled,
    )

    const channelOverride = currentPreviewPlatform ? channelTypeFromProvider[currentPreviewPlatform] : undefined
    const externalIntegrations = useExternalIntegrations().data?.external_integrations
    const integrationOverride = externalIntegrations?.find((i) => i.provider === currentPreviewPlatform)
    const blogpostIntegration = externalIntegrations?.find((integration) =>
      [
        ExternalIntegrationEnum.WordPress,
        ExternalIntegrationEnum.GoHighLevel,
        ExternalIntegrationEnum.Wix,
        ExternalIntegrationEnum.Shopify,
      ].includes(integration.provider),
    )

    const isDesignBlank = useMemo(() => {
      if (!!almanacFile?.is_design_blank) return true
      if (!isActive || !almanacFile?.design_content) return false

      const store = createTLStore({ shapeUtils })
      store.loadSnapshot(almanacFile.design_content as StoreSnapshot<any>)

      const editor = new Editor({
        store,
        shapeUtils: shapeUtils as any,
        tools: [],
        getContainer: () => document.createElement('div'),
      })

      try {
        return isBlankDesign(editor)
      } finally {
        editor.dispose()
      }
    }, [isActive, almanacFile?.design_content, almanacFile?.is_design_blank])

    const designContentHash = useMemo(() => {
      if (!isActive) return ''
      return sha1Hex(JSON.stringify(almanacFile?.design_content || []))
    }, [isActive, almanacFile?.design_content])

    // Removed 250ms delay - viewport renders immediately for instant navigation

    useDesignPreviewSync([almanacFile as unknown as AlmanacFileCalendarItem])

    useEffect(() => {
      if (!isActive || almanacFile?.asset_type !== 'Design' || !almanacFile?.key) return
      void prefetchDesignEditRequests(queryClient, workspaceId, almanacFile.key)
    }, [isActive, almanacFile?.asset_type, almanacFile?.key, workspaceId, queryClient])

    const isPortrait =
      almanacFile?.channel &&
      [
        ChannelType.InstagramPosts,
        DesignChannel.InstagramReel,
        DesignChannel.InstagramStory,
        DesignChannel.FacebookStory,
        DesignChannel.InstagramPost,
        DesignChannel.TikTokPost,
        DesignChannel.YoutubeShort,
      ].includes(almanacFile.channel)

    // default to parsing the image from the almanac file as a media object if no media objects are present
    let postWithMediaObjects = almanacFile?.scheduled_post
    if (almanacFile?.scheduled_post?.media_objects.length === 0 && almanacFile?.image_url) {
      postWithMediaObjects = {
        ...almanacFile?.scheduled_post,
        media_objects: [
          {
            id: 1,
            source_url: almanacFile?.image_url,
            thumbnail_url: almanacFile?.image_url,
            mime_type: 'image/png',
            metadata: { width: almanacFile?.width, height: almanacFile?.height },
            design_key: almanacFile?.key,
            position: 0,
          } as MediaObject,
        ],
      } as ScheduledPostShow
    }

    const hasMediaObjects = postWithMediaObjects?.media_objects && postWithMediaObjects.media_objects.length > 0
    const aspectRatio = almanacFile?.width && almanacFile?.height ? almanacFile?.width / almanacFile?.height : 1
    const canShowDesignPreview =
      almanacFile?.asset_type === 'Design' && (almanacFile?.image_url || almanacFile?.design_content) && !isDesignBlank

    // If the channel has a preview component, show it, otherwise show the plain design or article
    const showPostPreview = almanacFile?.channel && TYPES_WITH_PREVIEW.includes(almanacFile.channel)
    const showPlainDesignPreview = !showPostPreview && canShowDesignPreview
    const isArticlePreview = !showPostPreview && almanacFile?.asset_type === 'Article'
    const isBlogPostPreview = almanacFile?.channel === ChannelType.BlogPost
    const isNewsletterPreview = almanacFile?.channel === ChannelType.Newsletter
    const isDocumentPreview = isArticlePreview || isBlogPostPreview || isNewsletterPreview

    const expandPreviewHorizontally =
      !isDocumentPreview &&
      ((almanacFile?.width && almanacFile?.height && almanacFile?.width > almanacFile?.height) ||
        (!hasMediaObjects && !almanacFile?.design_content))

    const verticalSpace = windowHeight - windowHeightOffset
    const initialWidthForChannel = getMinWidthForChannel(channelOverride as DesignChannel)

    const chromeHeight = useMemo(() => {
      return (
        (postPreviewHeaderRef?.current?.clientHeight ?? 0) +
        (postPreviewContentRef?.current?.clientHeight ?? 0) +
        (postPreviewFooterRef?.current?.clientHeight ?? 0)
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      postPreviewHeaderRef?.current,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      postPreviewContentRef?.current,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      postPreviewFooterRef?.current,
      currentPreviewPlatform,
      almanacFile?.key,
    ])

    const constrainedHorizontalMaxWidth = isDocumentPreview ? undefined : '630px'
    const constrainedPortraitMaxWidth =
      !showVideoCrossPost && !isStoryChannel(almanacFile?.channel as DesignChannel)
        ? calculateMaxWidth(
            initialWidthForChannel - (hasRatingOverlay ? 30 : 0),
            verticalSpace,
            aspectRatio,
            chromeHeight,
          )
        : 550

    const postMaxWidth =
      !isDocumentPreview &&
      (expandPreviewHorizontally ? constrainedHorizontalMaxWidth : `${constrainedPortraitMaxWidth}px`)

    const style = useMemo<CSS.Properties>(() => {
      if (isMobile) {
        return {
          ['--aspect-ratio' as any]: showPlainDesignPreview ? aspectRatio : undefined,
        }
      } else {
        return {
          ['--aspect-ratio' as any]: showPlainDesignPreview ? aspectRatio : undefined,
          ['--post-max-width' as any]: postMaxWidth,
          ['--post-height' as any]:
            windowHeightOffset === DEFAULT_WINDOW_HEIGHT_OFFSET ? undefined : `${verticalSpace}px`,
        }
      }
    }, [isMobile, showPlainDesignPreview, aspectRatio, postMaxWidth, windowHeightOffset, verticalSpace])

    const designPreview = (
      <>
        {almanacFile?.image_url && !almanacFile?.image_url_generating ? (
          <img className={Styles.designContentImage} src={almanacFile.image_url} alt="design content" />
        ) : (
          <DesignContentPreview key={designContentHash} content={almanacFile?.design_content as StoreSnapshot<any>} />
        )}
      </>
    )

    const getContainerClass = (channel: string) => {
      switch (channel) {
        case DesignChannel.InstagramStory:
          return Styles.igStoryPreviewContainer
        case DesignChannel.FacebookStory:
          return Styles.fbStoryPreviewContainer
        case DesignChannel.InstagramReel:
          return Styles.igReelPreviewContainer
        case DesignChannel.TikTokPost:
          return Styles.tiktokPreviewContainer
        case DesignChannel.YoutubeShort:
          return Styles.youtubeShortPreviewContainer
        default:
          return undefined
      }
    }

    const getCustomPreviewContent = () => {
      if (canShowDesignPreview && isViewportReady && !hasMediaObjects) {
        return <div style={{ width: '100%', aspectRatio }}>{designPreview}</div>
      }

      return undefined
    }

    const getIntegrationValue = () => {
      if (integrationOverride !== undefined) {
        return integrationOverride || undefined
      }
      return almanacFile?.scheduled_post?.external_integration
    }

    const didGenerationFail = almanacFile?.did_generation_fail
    if (didGenerationFail) {
      return (
        <GenerationFailedPreview
          fileId={almanacFile.key}
          variant="fullScreenPreview"
          onClose={onClose}
          channel={almanacFile.channel}
          contentType={almanacFile.content_type}
        />
      )
    }

    if (isNewsletterPreview) {
      return (
        <NewsletterPreview
          almanacFile={almanacFile}
          ref={fRef}
          isActive={isActive}
          showHoverActionOverlay={showHoverActionOverlay}
          onClose={onClose}
        />
      )
    }

    if (isBlogPostPreview) {
      return (
        <>
          {isMobile ? null : <BlogPostPreviewChrome externalIntegration={blogpostIntegration} />}

          <div
            className={cx(Styles.preview, Styles.isArticlePreview, Styles.isBlogPostPreview)}
            style={style}
            ref={fRef}
            data-testid={`post-preview-${almanacFile?.key}`}
          >
            <ConditionalWrapper
              condition={showHoverActionOverlay}
              wrapper={(children) => (
                <EditInDocumentOverlay
                  almanacFileKey={almanacFile.key}
                  borderRadius="0 0 16px 16px"
                  position="top-right"
                  onClose={onClose}
                >
                  {children}
                </EditInDocumentOverlay>
              )}
            >
              <div className={Styles.blogPostPreviewContainer}>
                <ArticlePostPreview almanacFile={almanacFile} />
              </div>
            </ConditionalWrapper>
          </div>
        </>
      )
    }

    return (
      <div
        className={cx(Styles.preview, {
          [Styles.isDesignContentPreview]: showPlainDesignPreview,
          [Styles.isArticlePreview]: isArticlePreview,
          [Styles.isPortrait]: isPortrait,
        })}
        style={style}
        ref={fRef}
        data-testid={`post-preview-${almanacFile?.key}`}
      >
        {showPostPreview &&
        almanacFile?.scheduled_post?.id &&
        !isArticlePreview &&
        crossPostGroup.find((platform) => platform === currentPreviewPlatform) ? (
          <div className={Styles.multiPlatformPreview}>
            {crossPostGroup.map((platform: ExternalIntegrationEnum) => {
              const isActivePlatform =
                (currentPreviewPlatform || providerFromChannelType[almanacFile?.channel as ChannelType]) === platform
              const integration = externalIntegrations?.find((i) => i.provider === platform)

              const fileProvider = providerFromChannelType[almanacFile?.channel as ChannelType]

              let platformChannel: PostPreviewChannelType
              if (showVideoCrossPost) {
                const videoPlatformConfig = getVideoPlatformConfig(platform)
                platformChannel =
                  platform === fileProvider
                    ? (almanacFile?.channel as PostPreviewChannelType)
                    : ((videoPlatformConfig?.channel || channelTypeFromProvider[platform]) as PostPreviewChannelType)
              } else if (isStoryChannel(almanacFile?.channel as DesignChannel)) {
                const storyChannelFromProvider: Record<string, DesignChannel> = {
                  [ExternalIntegrationEnum.Instagram]: DesignChannel.InstagramStory,
                  [ExternalIntegrationEnum.Facebook]: DesignChannel.FacebookStory,
                }
                platformChannel =
                  platform === fileProvider
                    ? (almanacFile?.channel as PostPreviewChannelType)
                    : (storyChannelFromProvider[platform] as PostPreviewChannelType)
              } else {
                platformChannel =
                  platform === fileProvider
                    ? (almanacFile?.channel as PostPreviewChannelType)
                    : (channelTypeFromProvider[platform] as PostPreviewChannelType)
              }
              return (
                <div
                  key={platform}
                  className={cx(Styles.platformPreviewOverlay, {
                    [Styles.activePlatform]: isActivePlatform,
                    [Styles.inactivePlatform]: !isActivePlatform,
                  })}
                >
                  <Preview
                    channel={platformChannel}
                    loading={isLoading}
                    post={postWithMediaObjects as ScheduledPostShow}
                    integration={integration ?? almanacFile?.scheduled_post?.external_integration}
                    pollPreviewContentStream={false}
                    showVideoControls={showVideoControls}
                    videoFit={videoFit}
                    isActive={isActive}
                    containerClassName={cx(Styles.previewContainer, getContainerClass(platformChannel))}
                    photoBoxClassName={Styles.photoBox}
                    maxWidth={630}
                    customPreviewContent={getCustomPreviewContent()}
                    isDesignBlank={isDesignBlank}
                    isInsertingMedia={isInsertingMedia}
                    isTransitioning={isTransitioning}
                    showHoverActionOverlay={showHoverActionOverlay}
                    almanacFileKey={almanacFile?.key}
                    isGenerating={!!almanacFile?.is_generating}
                    onClose={onClose}
                    hideConnectButton={hideConnectButton}
                    alwaysTruncateCaption={alwaysTruncateCaption}
                    usernameContentSameLine
                    postPreviewHeaderRef={postPreviewHeaderRef}
                    postPreviewContentRef={postPreviewContentRef}
                    postPreviewFooterRef={postPreviewFooterRef}
                    activePlatform={currentPreviewPlatform}
                    onSeeMoreToggle={onSeeMoreToggle}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          showPostPreview && (
            <Preview
              channel={(channelOverride as PostPreviewChannelType) || (almanacFile?.channel as PostPreviewChannelType)}
              loading={isLoading}
              post={postWithMediaObjects as ScheduledPostShow}
              integration={getIntegrationValue()}
              pollPreviewContentStream={false}
              showVideoControls={showVideoControls}
              videoFit={videoFit}
              isActive={isActive}
              containerClassName={cx(
                Styles.previewContainer,
                getContainerClass(almanacFile?.channel as PostPreviewChannelType),
              )}
              photoBoxClassName={Styles.photoBox}
              maxWidth={630}
              customPreviewContent={getCustomPreviewContent()}
              isDesignBlank={isDesignBlank}
              isInsertingMedia={isInsertingMedia}
              isTransitioning={isTransitioning}
              showHoverActionOverlay={showHoverActionOverlay}
              almanacFileKey={almanacFile?.key}
              isGenerating={!!almanacFile?.is_generating}
              onClose={onClose}
              hideConnectButton={hideConnectButton}
              alwaysTruncateCaption={alwaysTruncateCaption}
              usernameContentSameLine
              postPreviewHeaderRef={postPreviewHeaderRef}
              postPreviewContentRef={postPreviewContentRef}
              postPreviewFooterRef={postPreviewFooterRef}
              activePlatform={currentPreviewPlatform}
              onSeeMoreToggle={onSeeMoreToggle}
            />
          )
        )}
        {showPlainDesignPreview && designPreview}
        {isArticlePreview && <ArticlePostPreview almanacFile={almanacFile} />}
      </div>
    )
  },
)

PostPreviewComponent.displayName = 'PostPreview'

const arePostPreviewPropsEqual = (prev: PostPreviewProps, next: PostPreviewProps) => {
  return (
    prev.almanacFile === next.almanacFile &&
    prev.isLoading === next.isLoading &&
    prev.showVideoControls === next.showVideoControls &&
    prev.videoFit === next.videoFit &&
    prev.isActive === next.isActive &&
    prev.channelOverride === next.channelOverride &&
    prev.integrationOverride === next.integrationOverride &&
    prev.setIsLoading === next.setIsLoading &&
    prev.onClose === next.onClose &&
    prev.onDelete === next.onDelete &&
    prev.filters === next.filters &&
    prev.fileChannel === next.fileChannel &&
    prev.startDate === next.startDate &&
    prev.previewPlatform === next.previewPlatform &&
    prev.onSelectPlatform === next.onSelectPlatform &&
    prev.isInsertingMedia === next.isInsertingMedia &&
    prev.isTransitioning === next.isTransitioning &&
    prev.showHoverActionOverlay === next.showHoverActionOverlay &&
    prev.almanacFile?.is_generating === next.almanacFile?.is_generating &&
    prev.alwaysTruncateCaption === next.alwaysTruncateCaption &&
    prev.hideConnectButton === next.hideConnectButton &&
    prev.hasRatingOverlay === next.hasRatingOverlay &&
    prev.windowHeightOffset === next.windowHeightOffset
  )
}

export const PostPreview = memo(PostPreviewComponent, arePostPreviewPropsEqual)
